import React from "react";

import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { faAdd } from "@fortawesome/pro-regular-svg-icons";
import {
  FieldPathByValue,
  useFieldArray,
  useFormContext,
} from "react-hook-form";

import { JsonSchemaEmptyState } from "src/aiNode/editorComponents/JsonSchemaEmptyState";
import { JsonSchemaRow } from "src/aiNode/editorComponents/JsonSchemaRow";
import { AiNodeJsonSchemaPropertyUI, AiNodeV2Form } from "src/aiNode/types";
import { getAiNodeNewProperty } from "src/aiNode/utils";
import { Card } from "src/base-components/Card";
import { Option } from "src/base-components/Select";
import { Button } from "src/design-system/Button";
import { getDefinitions } from "src/jsonSchema/schemaUtils";
import { JsonSchema } from "src/jsonSchema/types";

interface JsonSchemaEditorProps {
  immutable: boolean;
  rowsFormLocation: FieldPathByValue<
    AiNodeV2Form,
    AiNodeJsonSchemaPropertyUI[] | undefined
  >; // react-hook-forms path for binding
  typeOptions: Option[]; // for the type dropdown
  includeDefsAsTypeOptions?: boolean; // if true, will add custom type definitions ($defs) as type options
  schema?: JsonSchema;
  onOpenRawJsonView?: () => void;
  onClear?: () => void; // called when the last field is removed in the no-code view
  onChange?: () => void; // called when any field changes
}

export const JsonSchemaEditor: React.FC<JsonSchemaEditorProps> = ({
  immutable,
  typeOptions,
  rowsFormLocation,
  schema: externalSchema,
  includeDefsAsTypeOptions = true,
  onOpenRawJsonView,
  onClear,
  onChange,
}) => {
  const formProps = useFormContext<AiNodeV2Form>();
  const {
    fields,
    append: appendField,
    remove: removeField,
    move: moveField,
  } = useFieldArray({
    control: formProps.control,
    name: rowsFormLocation,
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const fieldToMove = fields.findIndex((field) => field.id === active.id);
      const fieldToInsert = fields.findIndex((field) => field.id === over.id);
      if (fieldToMove !== -1 && fieldToInsert !== -1) {
        moveField(fieldToMove, fieldToInsert);
      }
    }
  };

  const onAddField = () => {
    if (immutable) {
      return;
    }
    const newProperty = getAiNodeNewProperty();
    appendField(newProperty);
    onChange?.();
  };

  const onRemoveField = (index: number) => {
    if (immutable) {
      return;
    }
    const isLastField = fields.length === 1;
    removeField(index);
    if (isLastField) {
      onClear?.();
    }
    onChange?.();
  };

  // Create extended type options with custom JSON schema definitions
  const typeOptionsWithDefs = [
    ...typeOptions,
    ...(externalSchema != null && includeDefsAsTypeOptions
      ? createTypeOptionsFromDefs(externalSchema)
      : []),
  ];

  if (fields.length === 0) {
    return (
      <JsonSchemaEmptyState
        immutable={immutable}
        onAddField={onAddField}
        onOpenRawJsonView={() => {
          onOpenRawJsonView?.();
          // Automatically insert an empty field to change the no-code UI side appearance
          onAddField();
        }}
      />
    );
  }

  return (
    <Card className="w-full space-y-2 pt-4">
      <JsonSchemaHeader />
      <DndContext onDragEnd={handleDragEnd}>
        <SortableContext
          disabled={immutable}
          items={fields.map((row) => row.id)}
        >
          {fields &&
            fields.map((field, index) => {
              return (
                <JsonSchemaRow
                  key={field.id}
                  fieldData={field}
                  formLocation={`${rowsFormLocation}.${index}`}
                  handleRemove={() => onRemoveField(index)}
                  id={field.id}
                  immutable={immutable}
                  index={index}
                  typeOptions={typeOptionsWithDefs}
                  onChange={onChange}
                />
              );
            })}
        </SortableContext>
      </DndContext>
      <Button
        dataLoc="add-ai-node-jsonschema-field"
        isDisabled={immutable}
        iconLeft={faAdd}
        size="sm"
        variant="secondary"
        onClick={onAddField}
      >
        Add field
      </Button>
    </Card>
  );
};

const JsonSchemaHeader: React.FC = () => (
  // Replicating the structure of the JsonSchemaRow to align headers and fields
  <div className="flex gap-x-2 font-inter-normal-12px text-gray-500">
    {/* Empty span with w-5 is replacing the ReorderHandler */}
    <span className="w-5"></span>
    <span className="flex-1">Name</span>
    <span className="w-36">Type</span>
    {/* Empty span with w-[22px] is replacing the bin icon*/}
    <span className="w-[22px]"></span>
  </div>
);

const createTypeOptionsFromDefs = (schema?: JsonSchema): Option[] => {
  if (!schema?.$defs && !schema?.definitions) {
    return [];
  }
  const refPrefix = schema?.$defs != null ? "#/$defs" : "#/definitions";
  const definitions = getDefinitions(schema);
  if (!definitions) {
    return [];
  }

  return Object.keys(definitions).map((defName) => ({
    key: `${refPrefix}/${defName}`,
    value: `${defName}`, // "value" is actually the label
  }));
};
